/**
 * One popup: a single <dialog>, its strategy, its gates (completed action, page,
 * viewport), its delay and lifecycle, and its button actions. It decides whether it is
 * eligible, never when it is on screen; PopupManager arbitrates that.
 *
 * Load order: popup-dialog-strategy.js, this file, then popup-manager.js.
 */
window.UFT = window.UFT || {};
window.UFT.Popup =
	window.UFT.Popup ||
	(function () {
		class Popup {
			static DEFAULT_PRIORITY = 10;
			static FAILURE_FINGERPRINT = 'popup-prepare';
			static REDIRECT_FINGERPRINT = 'popup-redirect-refused';
			static WEB_PROTOCOLS = ['http:', 'https:'];
			static SELECTORS = {
				POPUP_BUTTON: '.popup__button',
				DISMISSING_LINK: '.popup__cta',
				STORAGE_KEY_BUTTON: '[data-storage-key]',
				MESSAGE_CONTAINER: '.popup__message',
				MESSAGE_CONTENT: '.popup__message-content',
				BUTTONS_CONTAINER: '.popup__buttons',
				// The only control still on screen once the loading state hides the buttons.
				FALLBACK_LINK: '.popup__fallback-link a',
			};
			static ATTRIBUTES = {
				REQUIRE_ACTION: 'requireAction',
				DELAY: 'delay',
				PRIORITY: 'priority',
				BLOCK_ID: 'blockId',
				ACTION: 'action',
				STORAGE_KEY: 'storageKey',
				STORAGE_SCOPE: 'storageScope',
				DISMISSAL_KEY: 'dismissalKey',
				DISMISSAL_SCOPE: 'dismissalScope',
				SEEN_KEY: 'seenKey',
				PRESENTATION: 'presentation',
				ERROR_MESSAGE: 'errorMessage',
				ALLOWED_PAGES: 'allowedPages',
				SHOW_ERROR_BACKGROUND: 'showErrorBackground',
				REDIRECT_URL: 'redirectUrl',
				REDIRECT_DELAY: 'redirectDelay',
				DESIGN_MODE: 'designMode',
			};
			static ACTIONS = { SHOW_MESSAGE: 'show_message', CLOSE: 'close', REDIRECT: 'redirect' };
			static TIMEOUTS = { SECONDS_TO_MS: 1000 };
			static MESSAGE_TYPES = { INFO: 'info', ERROR: 'error' };
			// The age gate renders no presentation attribute, so absent means modal.
			static PRESENTATIONS = { CORNER_CARD: 'corner_card' };
			static STORAGE_SCOPES = { SESSION: 'session' };
			static CSS_CLASSES = {
				MESSAGE_CONTENT_BASE: 'popup__message-content',
				MESSAGE_ERROR: 'popup__message--error',
				MESSAGE_ERROR_WITH_BG: 'popup__message--error-with-bg',
				MESSAGE_VISIBLE: 'is-visible',
				MOBILE_ONLY: 'popup--mobile-only',
				DESKTOP_ONLY: 'popup--desktop-only',
				SEEN: 'popup--seen',
				LOADING: 'loading',
			};
			// Also the TrueVault adapter's MOBILE_BREAKPOINT and the media blocks in
			// sections/popups.liquid; change all three together.
			static MEDIA_QUERIES = { MOBILE_BREAKPOINT: '(max-width: 767px)' };
			static SEPARATOR = '|';

			constructor(element, manager) {
				this.element = element;
				this.manager = manager;
				this.strategy = null;
				this.steppingAside = false;
				this.handleDialogClose = this.handleDialogClose.bind(this);
			}

			prepare() {
				try {
					// A hidden dialog must not spend its delay or queue; the manager re-prepares it if unhidden.
					if (this.element.hidden) {
						return;
					}

					// Re-prepared on every change to `hidden`; a second arming would queue this popup twice.
					if (this.strategy) {
						return;
					}

					// Removed here only. The manager re-checks the gates at open time, and a phone rotated in
					// between fails that check, but the element must stay so a geo-revealed dialog keeps its claim.
					if (!this.shouldShowForViewport()) {
						this.removeFromDOM();
						return;
					}

					if (!this.passesGates()) {
						return;
					}

					this.manager.ensurePageEventsBound();
					this.initializeDialogStrategy();

					setTimeout(() => {
						this.reportReadyToOpen();
					}, this.openDelayMs());
				} catch (error) {
					this.reportFailure(error);
				}
			}

			// The try in prepare() does not reach its timer callback, and a throw here would hold the
			// single open slot for the page view.
			reportReadyToOpen() {
				try {
					this.manager.requestOpen(this);
				} catch (error) {
					this.reportFailure(error);
				}
			}

			passesGates() {
				return (
					!this.hasUserCompletedRequiredAction() &&
					!this.dismissalApplies() &&
					!this.shouldBypassPopupForCurrentPage() &&
					this.shouldShowForViewport()
				);
			}

			hasUserCompletedRequiredAction() {
				const actionButton = this.element.querySelector(Popup.SELECTORS.STORAGE_KEY_BUTTON);
				if (!actionButton) {
					return false;
				}

				const storageKey = actionButton.dataset[Popup.ATTRIBUTES.STORAGE_KEY];
				const storageScope = actionButton.dataset[Popup.ATTRIBUTES.STORAGE_SCOPE];

				return this.isStorageKeySetToTrue(storageKey, storageScope);
			}

			// Not in the editor: it re-renders on every setting change, and a merchant who closed the
			// card once still has to judge the next change with it up. Never written there either, so
			// an editor session does not suppress the storefront in the same browser.
			dismissalApplies() {
				return !this.isInDesignMode() && this.hasUserDismissed();
			}

			// Keyed on the dialog, not the close button, so escape and the CTA record the dismissal too.
			hasUserDismissed() {
				const dismissalKey = this.dismissalKey();
				if (!dismissalKey) {
					return false;
				}

				return this.isStorageKeySetToTrue(dismissalKey, this.dismissalScope());
			}

			isInDesignMode() {
				return this.element.dataset[Popup.ATTRIBUTES.DESIGN_MODE] === 'true';
			}

			dismissalKey() {
				return this.element.dataset[Popup.ATTRIBUTES.DISMISSAL_KEY];
			}

			dismissalScope() {
				return this.element.dataset[Popup.ATTRIBUTES.DISMISSAL_SCOPE];
			}

			isStorageKeySetToTrue(storageKey, storageScope) {
				try {
					return this.storageForScope(storageScope).getItem(storageKey) === 'true';
				} catch (error) {
					console.warn('Could not check popup completion status:', error);
					return false;
				}
			}

			// Unset stays on localStorage, where the age gate's existing keys live.
			storageForScope(storageScope) {
				if (storageScope === Popup.STORAGE_SCOPES.SESSION) {
					return sessionStorage;
				}

				return localStorage;
			}

			shouldBypassPopupForCurrentPage() {
				const allowedPages = this.element.dataset[Popup.ATTRIBUTES.ALLOWED_PAGES];
				if (!allowedPages) {
					return false;
				}

				const currentPath = window.location.pathname;
				const allowedPagesList = allowedPages
					.split(Popup.SEPARATOR)
					.map((page) => page.trim())
					.filter((page) => page);

				return allowedPagesList.includes(currentPath);
			}

			shouldShowForViewport() {
				return !this.isViewportMismatch();
			}

			isViewportMismatch() {
				const isMobileOnly = this.element.classList.contains(Popup.CSS_CLASSES.MOBILE_ONLY);
				const isDesktopOnly = this.element.classList.contains(Popup.CSS_CLASSES.DESKTOP_ONLY);
				const isMobileViewport = this.isMobileViewport();

				return (isMobileOnly && !isMobileViewport) || (isDesktopOnly && isMobileViewport);
			}

			isMobileViewport() {
				return window.matchMedia(Popup.MEDIA_QUERIES.MOBILE_BREAKPOINT).matches;
			}

			removeFromDOM() {
				this.element.remove();
			}

			initializeDialogStrategy() {
				this.strategy = this.createDialogStrategy();
				this.strategy.setup(this.element, this);
				this.bindPopupCloseEvent();
			}

			hostsAccessibilityDrawer() {
				return this.strategy?.hostsAccessibilityDrawer() === true;
			}

			// Strategy listeners live on document and window and outlive the element. The theme editor
			// replaces the section element but not always its subtree, so a torn-down manager would
			// otherwise keep receiving closes from a dead popup.
			destroy() {
				this.unbindPopupCloseEvent();
				this.strategy?.cleanup();
				this.strategy = null;
			}

			// Presentation first, then capability. The only place the two axes meet.
			createDialogStrategy() {
				const strategies = window.UFT.PopupStrategies;

				if (this.isCornerCard()) {
					if (this.supportsNativeDialog()) {
						return new strategies.CornerCardDialogStrategy();
					}

					return new strategies.FallbackCornerCardDialogStrategy();
				}

				if (this.supportsNativeDialog()) {
					return new strategies.ModalDialogStrategy();
				}

				return new strategies.FallbackModalDialogStrategy();
			}

			isCornerCard() {
				return this.element.dataset[Popup.ATTRIBUTES.PRESENTATION] === Popup.PRESENTATIONS.CORNER_CARD;
			}

			supportsNativeDialog() {
				return typeof HTMLDialogElement === 'function' && typeof this.element.showModal === 'function';
			}

			bindPopupCloseEvent() {
				this.element.addEventListener('close', this.handleDialogClose);
			}

			unbindPopupCloseEvent() {
				this.element.removeEventListener('close', this.handleDialogClose);
			}

			// The manager closes a popup stepping aside for a required-action one; that close is
			// not the visitor's and must not write the dismissal.
			beginStepAside() {
				this.steppingAside = true;
			}

			// closePopup releases the lock before it hides, so a required-action dialog that closed
			// with the page still blocked was closed by the browser (a close request where closedby is
			// unsupported), not by Yes. The gate goes back up with the lock and the manager's slot intact.
			// Accepted cost: each reopen gets a fresh close watcher, so on Android browsers without
			// closedby every back gesture lands on the gate again and never navigates away.
			handleDialogClose() {
				if (this.requiresAction() && this.manager.hasBlockingPopup()) {
					this.displayDialog();
					return;
				}

				if (this.steppingAside) {
					this.steppingAside = false;
				} else {
					this.recordDismissal();
				}

				this.manager.popupClosed(this);
			}

			// A required-action popup is never dismissed, only completed, so the age gate
			// cannot write itself shut by being closed.
			recordDismissal() {
				if (this.requiresAction() || this.isInDesignMode()) {
					return;
				}

				const dismissalKey = this.dismissalKey();
				if (!dismissalKey) {
					return;
				}

				this.storeCompletionState(dismissalKey, this.dismissalScope());
			}

			// The delay serves the first sight only. Not a merchant setting: re-delaying on every page
			// is a defect, not a preference.
			openDelayMs() {
				if (this.hasBeenSeen()) {
					return 0;
				}

				return this.configuredDelayMs();
			}

			configuredDelayMs() {
				const delayInSeconds = parseInt(this.element.dataset[Popup.ATTRIBUTES.DELAY], 10) || 0;
				return delayInSeconds * Popup.TIMEOUTS.SECONDS_TO_MS;
			}

			hasBeenSeen() {
				const seenKey = this.seenKey();
				if (!seenKey) {
					return false;
				}

				return this.isStorageKeySetToTrue(seenKey, Popup.STORAGE_SCOPES.SESSION);
			}

			seenKey() {
				return this.element.dataset[Popup.ATTRIBUTES.SEEN_KEY];
			}

			priority() {
				return parseInt(this.element.dataset[Popup.ATTRIBUTES.PRIORITY], 10) || Popup.DEFAULT_PRIORITY;
			}

			requiresAction() {
				return this.element.dataset[Popup.ATTRIBUTES.REQUIRE_ACTION] === 'true';
			}

			show() {
				if (!this.isReadyToShow()) {
					return;
				}

				this.applySeenState();
				this.displayDialog();
				this.recordSeen();
			}

			// Before the dialog opens, because the entrance animation keys off the open attribute,
			// and before the sighting is recorded, because this answers for the previous appearance.
			applySeenState() {
				this.element.classList.toggle(Popup.CSS_CLASSES.SEEN, this.hasBeenSeen());
			}

			// Written at show time, not queue time, so a popup armed but never shown still gets its
			// unhurried first appearance.
			recordSeen() {
				const seenKey = this.seenKey();
				if (!seenKey || this.isInDesignMode()) {
					return;
				}

				this.storeCompletionState(seenKey, Popup.STORAGE_SCOPES.SESSION);
			}

			isReadyToShow() {
				return typeof this.strategy?.show === 'function';
			}

			displayDialog() {
				this.strategy.show();
			}

			close() {
				this.manager.closePopup(this);
			}

			isReadyToClose() {
				return typeof this.strategy?.close === 'function';
			}

			isOpen() {
				return this.strategy?.isOpen() === true;
			}

			hideDialog() {
				this.strategy.close();
			}

			releaseFocusTrap() {
				this.strategy?.releaseFocusTrap();
			}

			handleClick(event) {
				const dismissingLink = event.target.closest(Popup.SELECTORS.DISMISSING_LINK);
				if (dismissingLink) {
					this.handleDismissingLink(dismissingLink);
					return;
				}

				const button = event.target.closest(Popup.SELECTORS.POPUP_BUTTON);
				if (button) {
					this.handleButtonAction(button);
				}
			}

			// Recorded but left open: closing now would strand the visitor on a bare page, and the
			// manager takes the card down once the page hides (mail, phone and text hand-offs included).
			// A same-page anchor never hides the page, so the card closes for that itself, or a modal
			// would keep the target inert. Never the age gate: a link is not the decision it exists to take.
			handleDismissingLink(link) {
				this.recordDismissal();

				const destination = this.parsedUrl(link.href);
				if (this.requiresAction() || destination === null || !this.isSameDocumentFragment(destination)) {
					return;
				}

				this.close();
			}

			// Merchant-entered, so it may not parse; an anchor's href getter returns the raw attribute then.
			parsedUrl(href) {
				try {
					return new URL(href, window.location.origin);
				} catch {
					return null;
				}
			}

			handleButtonAction(button) {
				const storageKey = button.dataset[Popup.ATTRIBUTES.STORAGE_KEY];
				const action = button.dataset[Popup.ATTRIBUTES.ACTION];
				const actionOptions = {
					errorMessage: button.dataset[Popup.ATTRIBUTES.ERROR_MESSAGE],
					redirectUrl: button.dataset[Popup.ATTRIBUTES.REDIRECT_URL],
					redirectDelay: parseInt(button.dataset[Popup.ATTRIBUTES.REDIRECT_DELAY], 10) || 0,
				};

				if (storageKey) {
					this.storeCompletionState(storageKey, button.dataset[Popup.ATTRIBUTES.STORAGE_SCOPE]);
				}
				this.executeAction(action, actionOptions);
			}

			storeCompletionState(storageKey, storageScope) {
				try {
					this.storageForScope(storageScope).setItem(storageKey, 'true');
				} catch (error) {
					console.warn('Failed to store button action:', error);
				}
			}

			executeAction(action, options) {
				switch (action) {
					case Popup.ACTIONS.SHOW_MESSAGE: {
						this.handleShowMessageAction(options);
						break;
					}
					case Popup.ACTIONS.REDIRECT: {
						this.handleRedirectAction(options);
						break;
					}
					case Popup.ACTIONS.CLOSE: {
						this.close();
						break;
					}
					default: {
						throw new Error('#Popup - executeAction(): Unknown action');
					}
				}
			}

			handleShowMessageAction(options) {
				const { errorMessage } = options;
				if (!errorMessage) {
					return;
				}

				this.showMessage(errorMessage, Popup.MESSAGE_TYPES.ERROR);
			}

			handleRedirectAction(options) {
				const { errorMessage, redirectUrl, redirectDelay } = options;
				if (errorMessage) {
					this.showMessage(errorMessage, Popup.MESSAGE_TYPES.ERROR);
				}

				// Decided before the buttons hide: a refused or missing destination must leave the visitor
				// the buttons they had (WCAG 2.1.2).
				const destination = this.safeRedirectDestination(redirectUrl);
				if (!destination) {
					return;
				}

				this.showLoadingState();
				this.scheduleRedirect(destination, redirectDelay);
			}

			showLoadingState() {
				const buttonsContainer = this.element.querySelector(Popup.SELECTORS.BUTTONS_CONTAINER);
				if (!buttonsContainer) {
					return;
				}

				buttonsContainer.classList.add(Popup.CSS_CLASSES.LOADING);
				this.keepFocusInsideLoadingDialog();
			}

			// The loading class hides every button, the pressed one included, so focus falls to a <body>
			// showModal() has made inert. The decline message first, so what a departing visitor reads is
			// also announced (WCAG 4.1.3); then the fallback link; then the dialog's tabindex='-1' (WCAG 2.4.3).
			keepFocusInsideLoadingDialog() {
				if (!this.element.contains(document.activeElement)) {
					return;
				}

				const target =
					this.visibleMessageContainer() ?? this.element.querySelector(Popup.SELECTORS.FALLBACK_LINK) ?? this.element;
				target.focus();
			}

			visibleMessageContainer() {
				const messageContainer = this.element.querySelector(Popup.SELECTORS.MESSAGE_CONTAINER);
				if (!messageContainer?.classList.contains(Popup.CSS_CLASSES.MESSAGE_VISIBLE)) {
					return null;
				}

				return messageContainer;
			}

			// The delay is always a number: handleButtonAction reads it with parseInt(...) || 0.
			scheduleRedirect(destination, redirectDelayInSeconds) {
				setTimeout(() => {
					window.location.href = destination;
				}, redirectDelayInSeconds * Popup.TIMEOUTS.SECONDS_TO_MS);
			}

			// Merchant-supplied, and Shopify's url setting does not refuse a javascript: value,
			// so only a web destination that will actually navigate is followed.
			safeRedirectDestination(redirectUrl) {
				if (!redirectUrl) {
					return null;
				}

				try {
					const url = new URL(redirectUrl, window.location.origin);
					if (Popup.WEB_PROTOCOLS.includes(url.protocol) && !this.isSameDocumentFragment(url)) {
						return url.href;
					}
				} catch {
					// Only genuinely unparsable input lands here; with a base origin new URL() resolves almost anything.
				}

				this.reportRefusedRedirect(redirectUrl);
				return null;
			}

			// A fragment on the current URL is not a navigation, so the loading state would never be
			// relieved by the page going away.
			isSameDocumentFragment(url) {
				// hash is '' for a bare trailing '#' too, which is still a fragment navigation; only the
				// serialized href tells them apart.
				if (url.href === this.withoutFragment(url)) {
					return false;
				}

				return this.withoutFragment(url) === this.withoutFragment(new URL(window.location.href));
			}

			withoutFragment(url) {
				const copy = new URL(url.href);
				copy.hash = '';
				return copy.href;
			}

			reportRefusedRedirect(redirectUrl) {
				window.UFT?.FailureReporter?.report(Popup.REDIRECT_FINGERPRINT, 'Popup redirect refused', {
					extra: {
						blockId: this.element.dataset[Popup.ATTRIBUTES.BLOCK_ID],
						redirectUrl,
					},
				});
			}

			showMessage(message, type = Popup.MESSAGE_TYPES.INFO) {
				const messageContainer = this.element.querySelector(Popup.SELECTORS.MESSAGE_CONTAINER);
				const messageContent = this.element.querySelector(Popup.SELECTORS.MESSAGE_CONTENT);

				if (!messageContainer || !messageContent) {
					return;
				}

				messageContent.textContent = message;
				messageContent.className = Popup.CSS_CLASSES.MESSAGE_CONTENT_BASE;

				if (type === Popup.MESSAGE_TYPES.ERROR) {
					this.applyErrorMessageStyles(messageContent);
				}

				messageContainer.classList.add(Popup.CSS_CLASSES.MESSAGE_VISIBLE);
			}

			applyErrorMessageStyles(messageContent) {
				messageContent.classList.add(Popup.CSS_CLASSES.MESSAGE_ERROR);

				if (this.element.dataset[Popup.ATTRIBUTES.SHOW_ERROR_BACKGROUND] === 'true') {
					messageContent.classList.add(Popup.CSS_CLASSES.MESSAGE_ERROR_WITH_BG);
				}
			}

			// A bfcache restore can bring back a redirect's loading or error state, so every state is cleared.
			resetState() {
				const resetHidesFocusHolder = this.resetHidesFocusHolder();

				this.resetButtonsContainerState();
				this.resetMessageContainerState();

				// The dialog carries tabindex='-1' for exactly this.
				if (resetHidesFocusHolder) {
					this.element.focus();
				}
			}

			// The two nodes keepFocusInsideLoadingDialog can pick.
			resetHidesFocusHolder() {
				const focusHolder = document.activeElement;
				if (focusHolder === null) {
					return false;
				}

				return (
					focusHolder === this.visibleMessageContainer() ||
					focusHolder === this.element.querySelector(Popup.SELECTORS.FALLBACK_LINK)
				);
			}

			resetButtonsContainerState() {
				const buttonsContainer = this.element.querySelector(Popup.SELECTORS.BUTTONS_CONTAINER);
				if (!buttonsContainer) {
					return;
				}

				buttonsContainer.classList.remove(Popup.CSS_CLASSES.LOADING);
			}

			// Cleared, not just unstyled: role=status forbids display:none and is-visible controls only
			// a margin, so the message would survive the restore.
			resetMessageContainerState() {
				const messageContainer = this.element.querySelector(Popup.SELECTORS.MESSAGE_CONTAINER);
				if (!messageContainer) {
					return;
				}

				messageContainer.classList.remove(Popup.CSS_CLASSES.MESSAGE_VISIBLE);

				const messageContent = this.element.querySelector(Popup.SELECTORS.MESSAGE_CONTENT);
				if (!messageContent) {
					return;
				}

				messageContent.textContent = '';
				messageContent.className = Popup.CSS_CLASSES.MESSAGE_CONTENT_BASE;
			}

			// A popup that cannot evaluate its own gates stays closed and quiet, so one broken
			// popup never suppresses the others on the page.
			reportFailure(error) {
				window.UFT?.FailureReporter?.report(Popup.FAILURE_FINGERPRINT, 'Popup failed to prepare', {
					extra: {
						blockId: this.element.dataset[Popup.ATTRIBUTES.BLOCK_ID],
						message: error?.message,
					},
				});
			}
		}

		return Popup;
	})();
